<template>
  <div class="card mt-5 claim-card">
    <div class="card-image p-2">
      <figure class="image is-4by3" :style="{ backgroundImage: 'url(' + CodeDetails + ')' }"></figure>
    </div>
    <div class="card-content pt-0">
      <div class="media">
        <div class="media-content">
          <p class="is-size-4 has-text-info has-text-centered is-family-secondary mb-1">
            {{ $t("claimContainer.claim") }}
          </p>
          <div class="has-text-centered" v-if="loading">
            <Loading isSmall isInlineBlock />
          </div>
          <form v-else @submit.prevent="handleClaim" autocomplete="off">
            <input autocomplete="false" name="hidden" type="text" style="display: none" />
            <div class="level mt-2 mb-1">
              <input
                type="text"
                placeholder="CXXXXXX"
                v-model="code"
                required
                maxlength="7"
                pattern="[cC][A-Za-z0-9]{6}"
                class="has-text-centered is-size-4-desktop pb-1 pt-1"
                id="claim_id"
              />
            </div>
            <div class="input-icon">
              <input
                type="text"
                placeholder="Container PIN"
                v-model="pin"
                pattern="[0-9]{1,3}"
                maxlength="3"
                inputmode="numeric"
                class="has-text-centered is-size-4-desktop pb-1 pt-1"
                id="pin_input"
              />
              <span class="info-icon" @click="showPinTooltip = !showPinTooltip">i</span>
            </div>
            <div v-if="showPinTooltip" class="pin-tooltip">
              {{ $t('claimContainer.new-pin') }}
            </div>
            <div class="has-text-centered pt-2 pb-1">
              <button type="submit" :disabled="submitting" class="button is-info is-size-6 pt-1 pb-1" id="claim_button">
                <p class="is-family-secondary is-size-6 pb-2 pt-2">
                  {{ $t("enter") }}
                </p>
              </button>
              <Loading v-if="submitting" isSmall isInlineBlock />
            </div>
            <div v-if="message.type == 'err'">
              <p class="has-text-centered is-size-5 has-text-danger-dark">
                {{ message.msg }}
              </p>
            </div>
            <div v-if="message.type == 'msg'" class="has-text-centered is-size-5 has-text-success">
              <p>{{ message.msg }}</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { Loading } from "@/revamp/components";
import { CodeDetails } from "@/revamp/assets/images";
import { useFirebase } from "@/revamp/composables";
import { AuthService } from "@/revamp/firebase/config";

import { logger } from "@/revamp/utils";
import constants from "@/revamp/utils/constants";

const props = defineProps({
  verify: Function,
  sendVerification: Function,
});

const APIService = useFirebase();
const { t } = useI18n();

const message = ref({ type: null, msg: "" });
const code = ref("");
const pin = ref("");
const user = ref();

const showPinTooltip = ref(false);
const submitting = ref(false);
const loading = ref(false);

onMounted(() => {
  const setup = async () => {
    loading.value = true;
    user.value = AuthService.currentUser;
    loading.value = false;
  };
  setup();
});

const isMobile = function () {
  let check = /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
  if (!check)
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
  if (!check) check = window.innerWidth <= 800 || window.innerHeight <= 600;
  return check;
};

const verifyQR = async () => {
  try {
    let response = await APIService.quickClaimCheck(code.value); // C01VHMD
    if (!response) {
      logger.error(`ClaimContainer.VerifyQR: Code ${code.value} does not exist`, {
        code: code.value,
        user: user.value.uid,
      });
      message.value = {
        type: "err",
        msg: t("claimContainer.error.invalid-not-found"),
      };
      return null;
    }

    if (response.isReturned) {
      logger.error(`ClaimContainer.VerifyQR: Code ${code.value} is already claimed`, {
        code: code.value,
        user: user.value.uid,
      });
      message.value = {
        type: "err",
        msg: t("claimContainer.already-claimed"),
      };
      return null;
    }

    if (response.checkDigits && pin.value) {
      if (!/^\d{1,3}$/.test(pin.value)) {
        message.value = { type: "err", msg: t("claimContainer.pin-format-error") };
        submitting.value = false;
        return;
      }
      pin.value = pin.value.padStart(3, "0");
    }

    return response;
  } catch (e) {
    logger.error(`ClaimContainer.VerifyQR: Failed to verify code ${code.value}. ${e.toString()}`, {
      code: code.value,
      user: user.value.uid,
    });
    message.value = {
      type: "err",
      msg: t("claimContainer.oops"),
    };
    return null;
  }
};

const handleClaim = async () => {
  submitting.value = true;

  message.value = { type: null, msg: "" };

  if (!user.value.emailVerified) {
    message.value = { type: "err", msg: t("claimContainer.verify") };
    props.verify({
      title: t("claimContainer.notice"),
      body: t("claimContainer.verify-email"),
      buttons: [
        {
          text: t("claimContainer.close"),
          action: () => {
            props.verify({});
          },
        },
        {
          text: t("claimContainer.send"),
          action: () => {
            props.verify({});
            props.sendVerification();
          },
        },
      ],
    });

    submitting.value = false;
    return;
  }

  code.value = code.value.toUpperCase();
  const verifyResponse = await verifyQR();
  if (!verifyResponse) {
    submitting.value = false;
    return;
  }

  const claimResponse = await APIService.claimCode(
    code.value,
    user.value.email,
    { source: "manual", codeForm: "code", platform: isMobile() ? "mobile web" : "web" },
    pin.value
  );
  if (!claimResponse || claimResponse.error) {
    submitting.value = false;

    if (claimResponse.code === constants.ERROR_CODES.INVALID_PIN) {
      message.value = {
        type: "err",
        msg: t("claimContainer.incorrect-pin"),
      };
      return;
    }

    message.value = {
      type: "err",
      msg: t("claimContainer.error.generic"),
    };
    return;
  }

  submitting.value = false;
  code.value = "";
  pin.value = "";
  message.value = {
    type: "msg",
    msg: t("claimContainer.container-success"),
  };
  return;
};
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";

#claim_id {
  width: 100%;
}
#pin_input {
  width: 100%;
}
.input-icon {
  position: relative;
  display: inline-block;
  width: 100%;
}
.input-icon input {
  width: 100%;
  padding-right: 30px;
  box-sizing: border-box;
}
.info-icon {
  position: absolute;
  right: 8px; /* adjust as needed */
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #032673;
  color: $cloud;
  font-weight: bold;
  text-align: center;
  line-height: 18px;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
}
.pin-tooltip {
  margin-top: 8px;
  padding: 10px 12px;
  background-color: $denim;
  color: $cloud;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: center;
}
#claim_button {
  background-color: #032673;
}

.claim-card {
  height: -webkit-fit-content;
  height: -moz-fit-content;
  height: fit-content;
  background-color: darken($cloud, 5%);

  .card-image {
    figure {
      width: 100%;
      height: 0;
      padding-top: 100%;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  }

  .card-content {
    .media {
      .media-content {
        .app-badges {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-around;
          width: 100%;

          a {
            display: inline-flex;
            align-items: center;
            width: 40%;
            height: 100%;

            img {
              width: 100%;
              height: 100%;
            }
          }
        }
      }
    }
  }
}
</style>
